// src/pages/components/OwnerImageUploader.jsx
//
// "Bilder till IM" — one slot per asset role the IM layout knows about.
// Each slot is either populated (thumbnail + Byt / Ta bort) or empty
// (Ladda upp). Uploaded images override the web-scraped assets in the
// backend renderer (``render.py::_resolve_assets``), so this is the
// single control surface a seller / broker uses to fill in what the
// scrape missed and override what it got wrong.
//
// Roles + endpoint contracts must match:
//   backend/IM_studio/models.py :: WorkspaceOwnerImage.ROLE_CHOICES
//   backend/IM_studio/views.py  :: owner_images / owner_image_delete
//   backend/IM_studio/urls.py   :: /IM_studio/company/<orgnr>/owner-images/
//
// A single file input is reused across all roles — reset via a key bump
// so uploading the same filename twice still triggers onChange.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { request } from "../../services/server";

export const OWNER_IMAGE_ROLES = [
  { value: "hero",                label: "Hero / omslagsbild" },
  { value: "logo",                label: "Logotyp" },
  { value: "team_image",          label: "Team-bild" },
  { value: "office_image",        label: "Kontor / lokal" },
  { value: "product_image",       label: "Produkt / tjänst" },
  { value: "certification_image", label: "Certifikat / utmärkelse" },
  { value: "section_image",       label: "Sektionsbild" },
];
const DEFAULT_ROLES = OWNER_IMAGE_ROLES;

// Absolute URL not required for <img src>: relative /media/... paths
// resolve against the frontend origin, which is proxied to the backend
// in dev via the same auth session.
//
// ``visibleRoleValues`` (optional Set<string>) narrows the role grid to
// only the slots the seller actually opted into via the intake wizard.
// When omitted, every allowed role from the backend is shown — preserves
// legacy call sites that embedded the uploader without a wizard.
function OwnerImageUploader({ orgnr, visibleRoleValues }) {
  const [byRole, setByRole] = useState({});
  const [allowedRoles, setAllowedRoles] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [busyRole, setBusyRole] = useState(null);
  const [error, setError] = useState("");
  const [uploadKey, setUploadKey] = useState(0);
  const pendingRoleRef = useRef(null);
  const fileInputRef = useRef(null);

  const endpoint = orgnr
    ? `/IM_studio/company/${encodeURIComponent(orgnr)}/owner-images/`
    : null;

  const load = useCallback(async () => {
    if (!endpoint) return;
    setLoading(true);
    setError("");
    try {
      const data = await request(endpoint);
      const next = {};
      for (const img of data?.images || []) {
        if (img?.role) next[img.role] = img;
      }
      setByRole(next);
      if (Array.isArray(data?.allowed_roles) && data.allowed_roles.length) {
        setAllowedRoles(data.allowed_roles);
      }
    } catch (e) {
      setError(e?.message || "Kunde inte hämta bilder.");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const triggerUpload = (role) => {
    if (!orgnr || busyRole) return;
    pendingRoleRef.current = role;
    // Bump the key to reset the input so re-selecting the same file works.
    setUploadKey((k) => k + 1);
    // The input mount happens after this render; poll one tick.
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    const role = pendingRoleRef.current;
    pendingRoleRef.current = null;
    if (!file || !role || !endpoint) return;

    setBusyRole(role);
    setError("");
    try {
      const form = new FormData();
      form.append("role", role);
      form.append("image", file);
      const created = await request(endpoint, {
        method: "POST",
        body: form,
        formData: true,
      });
      setByRole((prev) => ({ ...prev, [role]: created }));
    } catch (err) {
      setError(err?.message || "Uppladdningen misslyckades.");
    } finally {
      setBusyRole(null);
    }
  };

  const removeRole = async (role) => {
    if (!orgnr || busyRole) return;
    if (!window.confirm(`Ta bort uppladdad bild för rollen "${role}"?`)) return;
    setBusyRole(role);
    setError("");
    try {
      await request(
        `${endpoint}${encodeURIComponent(role)}/`,
        { method: "DELETE" },
      );
      setByRole((prev) => {
        const next = { ...prev };
        delete next[role];
        return next;
      });
    } catch (err) {
      setError(err?.message || "Kunde inte ta bort bilden.");
    } finally {
      setBusyRole(null);
    }
  };

  if (!orgnr) {
    return (
      <fieldset className="risk-card">
        <legend>Bilder till IM</legend>
        <p className="risk-help">
          Bilder kan laddas upp först när organisationsnummer är kopplat till
          bolaget.
        </p>
      </fieldset>
    );
  }

  return (
    <fieldset className="risk-card">
      <legend>Bilder till IM</legend>
      <p className="risk-help">
        Ladda upp foton på team, kontor, produkter och certifikat som ska
        användas i IM:et. Uppladdade bilder ersätter automatiskt de som
        systemet hämtat från er hemsida — så här fyller ni i luckor och
        byter ut det som blivit fel. Rekommenderat format: JPG eller PNG,
        upp till 20 MB per bild.
      </p>

      {loading && <p className="risk-help">Hämtar bilder…</p>}
      {error && (
        <p
          className="risk-help"
          style={{ color: "#c0261a", fontWeight: 600 }}
        >
          {error}
        </p>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 16,
          marginTop: 12,
        }}
      >
        {allowedRoles
          .filter(({ value }) => !visibleRoleValues || visibleRoleValues.has(value))
          .map(({ value, label }) => {
          const img = byRole[value];
          const isBusy = busyRole === value;
          return (
            <div
              key={value}
              style={{
                border: "1px solid #e2e2df",
                borderRadius: 6,
                padding: 12,
                background: "#fafaf7",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1c1917",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  background: "#f0efe9",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {img?.url ? (
                  <img
                    src={img.url}
                    alt={label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit:
                        value === "logo" ||
                        value === "certification_image"
                          ? "contain"
                          : "cover",
                      background:
                        value === "logo" ? "#ffffff" : undefined,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      color: "#78716c",
                      fontSize: 12,
                    }}
                  >
                    Ingen bild uppladdad
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => triggerUpload(value)}
                  disabled={isBusy}
                  style={{
                    flex: 1,
                    minWidth: 90,
                    padding: "6px 10px",
                    fontSize: 12,
                    cursor: isBusy ? "wait" : "pointer",
                  }}
                >
                  {isBusy
                    ? "…"
                    : img?.url
                    ? "Byt bild"
                    : "Ladda upp"}
                </button>
                {img?.url && (
                  <button
                    type="button"
                    onClick={() => removeRole(value)}
                    disabled={isBusy}
                    style={{
                      padding: "6px 10px",
                      fontSize: 12,
                      color: "#c0261a",
                      cursor: isBusy ? "wait" : "pointer",
                    }}
                  >
                    Ta bort
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Single hidden file input shared across all role cards. */}
      <input
        key={uploadKey}
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFilePicked}
        style={{ display: "none" }}
      />
    </fieldset>
  );
}

export default OwnerImageUploader;
